import React, { useState, useEffect, useCallback, useMemo, forwardRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { TableVirtuoso } from 'react-virtuoso';
import dayjs from 'dayjs';

import { Page } from 'components';
import { callApi } from 'utils';
import { allTrackers } from 'consts';

const columns = [
  { name: 'parent', label: '订单ID' },
  { name: 'id', label: '任务ID' },
  { name: 'date', label: '执行日期' },
  { name: 'tracker', label: '检测公司', format: (val) => allTrackers[val] },
  { name: 'stay', label: '停留时间', numeric: true },
  { name: 'landing', label: '到达次数', numeric: true },
  { name: 'landingRate', label: '到达率', numeric: true },
  { name: 'bounceRate', label: '跳出率', numeric: true },
  //{ name: 'jiagou', label: '加购数' },
];

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => <TableContainer {...props} ref={ref} />),
  Table: (props) => <Table {...props} size="small" sx={{ px: 1, borderCollapse: 'separate', tableLayout: 'fixed' }} />,
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

const headerContent = () => (
  <TableRow>
    {columns.map((col) => (
      <TableCell
        key={col.name}
        variant="head"
        align={col.numeric ? 'right' : 'center'}
        sx={{ backgroundColor: 'background.paper' }}
      >
        {col.label}
      </TableCell>
    ))}
  </TableRow>
);

const rowContent = (_index, row) =>
  columns.map((col) => (
    <TableCell
      key={col.name}
      align={col.numeric ? 'right' : 'center'}
    >
      {col.format ? col.format(row[col.name]) : row[col.name]}
    </TableCell>
  ));

const VirtuosoTableFooter = () => (
  <TableFooter>
    <TableCell
      colSpan={columns.length}
      sx={{
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      Loading...
    </TableCell>
  </TableFooter>
);

const defaultFilter = {idtype: 'parent', id: '', tracker: '', start_date: null, end_date: null};

const TrackerReports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [last, setLast] = useState('');
  const [filter, setFilter] = useState(() => Object.fromEntries(
    Object.keys(defaultFilter).map(k => {
      let v;
      if (k.endsWith('_date')) {
        v = searchParams.get(k.slice(0, -5));
        v = v ? dayjs(v) : null;
      } else {
        v = searchParams.get(k) || defaultFilter[k];
      }
      return [k, v];
    })
  ));

  const handleFilter = useCallback((ev) => setFilter(prev => ({...prev, [ev.target.name]: ev.target.value})), []);
  const changeDate = (name, val) => setFilter(prev => ({...prev, [name]: val}));
  const submitFilter = () => {
    const sp = new URLSearchParams();
    if (filter.id) {
      sp.set('idtype', filter.idtype);
      sp.set('id', filter.id.trim());
    }
    filter.tracker !== '' && sp.set('tracker', filter.tracker);
    filter.start_date && sp.set('start', filter.start_date.format('YYYY-MM-DD'));
    filter.end_date && sp.set('end', filter.end_date.format('YYYY-MM-DD'));
    if (searchParams.toString() !== sp.toString()) {
      setRows([]);
      setLast('');
      setSearchParams(sp);
    }
  };
  const resetFilter = () => setFilter({...defaultFilter});

  const fetchData = useCallback((last) => {
    callApi(`/reports/trackers?last=${last}&${searchParams.toString()}`)
      .then((res) => {
        if (res.length) {
          setRows((prev) => [...prev, ...res]);
          const lastRow = res[res.length-1];
          setLast(`${lastRow.date}:${lastRow.id}`);
        } else {
          setLast('na'); // not anymore
        }
      })
      .catch(console.error)
  }, [searchParams]);

  const loadMore = useCallback(() => (last !== 'na') && fetchData(last), [last, fetchData]);

  useEffect(() => void(fetchData('')), [fetchData]);

  const tableComponents = useMemo(() => ({
    ...VirtuosoTableComponents,
    TableFooter: last === 'na' ? null : VirtuosoTableFooter,  // todo: incorrect
  }), [last]);

  return (
    <Page title='指标数据' sx={{display: 'flex', flexDirection: 'column', height: '100%'}} >
      <Toolbar sx={{mt: -1}}>
        <Select
          name="idtype"
          value={filter.idtype}
          size="small"
          onChange={handleFilter}
          sx={{ mr: 0.5, '& .MuiSelect-select': {py: '6px'} }}
        >
          <MenuItem value='parent'>订单ID</MenuItem>
          <MenuItem value='id'>任务ID</MenuItem>
        </Select>:
        <OutlinedInput
          sx={{mx: 0.5, p: 0, width: 120}}
          name="id"
          value={filter.id}
          size="small"
          inputProps={{ style: {padding: '6px'} }}
          onChange={handleFilter}
        />
        <Typography>检测公司:</Typography>
        <Select
          name="tracker"
          value={filter.tracker}
          size="small"
          onChange={handleFilter}
          displayEmpty
          sx={{ mx: 0.5, '& .MuiSelect-select': {py: '6px'} }}
        >
          <MenuItem value={''}>--请选择--</MenuItem>
          {allTrackers.map((name, idx) => (
          <MenuItem value={idx} key={idx}>{name}</MenuItem>
          ))}
        </Select>
        <Typography sx={{mr: 0.5}}>排期时间:</Typography>
        <DatePicker
          sx={{ width: 150, '& .MuiInputBase-input': {p: '6px'} }}
          value={filter.start_date}
          views={['year', 'month', 'day']}
          onChange={val => changeDate('start_date', val)}
          dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
        />
        <span style={{margin: '0 4px', display: 'inline-flex', alignItems: 'center' }}>~</span>
        <DatePicker
          sx={{ width: 150, '& .MuiInputBase-input': {p: '6px'} }}
          value={filter.end_date}
          views={['year', 'month', 'day']}
          minDate={filter.start_date}
          onChange={val => changeDate('end_date', val)}
          dayOfWeekFormatter={(_, date) => dayjs(date).day() || 7}
        />
        <Button
          sx={{ mx: 0.5 }}
          variant="outlined"
          size="small"
          onClick={submitFilter}
        >搜索</Button>
        <Button
          variant="outlined"
          size="small"
          onClick={resetFilter}
        >重置</Button>
      </Toolbar>
      <Paper sx={{p: 0, m: 2, mt: -1, border: 0, flexGrow: 1}}>
        <TableVirtuoso
          data={rows}
          components={tableComponents}
          fixedHeaderContent={headerContent}
          itemContent={rowContent}
          endReached={loadMore}
          increaseViewportBy={100}
        />
      </Paper>
    </Page>
  );
};

export default TrackerReports;
